import type { PurchaseOrderSummary } from '@/types'
import type { VendorTone } from './vendorStatus'

/** Plain-language purchase-order vocabulary (F-FE-N97).
 *
 *  One mapping for the whole Purchasing surface. The backend's states are
 *  accounting words ("ordered", "partially_received"); the people receiving a
 *  chai delivery need lifecycle words ("Sent — awaiting delivery", "Partly
 *  delivered"). "Draft" is deliberately absent: the backend never writes it
 *  (creation lands in pending_approval or approved), and the old UI's "Create
 *  draft" button promised a state that did not exist. Only legacy rows from
 *  the retired transfer-order remap can still hold 'draft'.
 */

export type PoBucket = 'needs_approval' | 'ready_to_send' | 'arriving' | 'history'
export type PoPrimaryAction = 'approve' | 'send' | 'receive' | null

export function poRef(id: string): string {
  const compact = id.replace(/-/g, '')
  return compact.length >= 8 ? compact.slice(0, 8).toUpperCase() : id.slice(0, 12)
}

export function poStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    pending_approval: 'Waiting for approval',
    approved: 'Ready to send',
    ordered: 'Sent — awaiting delivery',
    partially_received: 'Partly delivered',
    received: 'Delivered',
    cancelled: 'Cancelled',
    draft: 'Not sent yet', // legacy rows only — the app no longer writes it
  }
  return labels[status] ?? status.replace(/_/g, ' ')
}

export function poStatusTone(status: string): VendorTone {
  switch (status) {
    case 'pending_approval':
    case 'partially_received':
      return 'warning'
    case 'approved':
    case 'ordered':
      return 'info'
    case 'received':
      return 'success'
    default:
      return 'neutral'
  }
}

export function poBucket(status: string): PoBucket {
  switch (status) {
    case 'pending_approval':
      return 'needs_approval'
    case 'approved':
    case 'draft':
      return 'ready_to_send'
    case 'ordered':
    case 'partially_received':
      return 'arriving'
    default:
      return 'history'
  }
}

/** Exactly ONE next step per order — the row shows it, everything else lives
 *  in the detail sheet. A non-admin gets NO action on pending_approval: the
 *  approve route is admin-only, and rendering the button anyway (as the old
 *  table did) handed them a guaranteed 403. The pill already says why. */
export function poPrimaryAction(status: string, isAdmin: boolean): PoPrimaryAction {
  switch (status) {
    case 'pending_approval':
      return isAdmin ? 'approve' : null
    case 'approved':
    case 'draft':
      return 'send'
    case 'ordered':
    case 'partially_received':
      return 'receive'
    default:
      return null
  }
}

export const PO_PRIMARY_ACTION_LABEL: Record<Exclude<PoPrimaryAction, null>, string> = {
  approve: 'Approve',
  send: 'Send order',
  receive: 'Receive delivery',
}

const DAY_MS = 24 * 60 * 60 * 1000

function daysFromToday(iso: string, today: Date): number | null {
  const d = new Date(iso.slice(0, 10) + 'T00:00:00')
  if (Number.isNaN(d.getTime())) return null
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  return Math.round((d.getTime() - t.getTime()) / DAY_MS)
}

export interface PoExpectedInfo {
  text: string
  tone: VendorTone
  /** Sort key inside the arriving bucket: negative = days late. */
  daysUntil: number | null
}

function humanDate(iso: string): string {
  const d = new Date(iso.slice(0, 10) + 'T00:00:00')
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

/** What the row says under the vendor name, per lifecycle stage. */
export function poExpectedInfo(
  o: Pick<PurchaseOrderSummary, 'status' | 'expected_date' | 'receipt_date'>,
  today: Date,
): PoExpectedInfo {
  if (o.status === 'received') {
    return { text: o.receipt_date ? `Delivered ${humanDate(o.receipt_date)}` : 'Delivered', tone: 'success', daysUntil: null }
  }
  if (o.status === 'cancelled') {
    return { text: 'Cancelled', tone: 'neutral', daysUntil: null }
  }
  const open = o.status === 'ordered' || o.status === 'partially_received'
  if (!o.expected_date) {
    return { text: open ? 'No delivery date' : 'No date set', tone: 'neutral', daysUntil: null }
  }
  const d = daysFromToday(o.expected_date, today)
  if (d == null) return { text: humanDate(o.expected_date), tone: 'neutral', daysUntil: null }
  if (!open) {
    // Not sent yet — the date is a plan, not a promise; never call it late.
    return { text: `Expected ${humanDate(o.expected_date)}`, tone: 'neutral', daysUntil: d }
  }
  if (d < 0) {
    const late = -d
    return {
      text: late === 1 ? `1 day late — expected ${humanDate(o.expected_date)}` : `${late} days late — expected ${humanDate(o.expected_date)}`,
      tone: 'danger',
      daysUntil: d,
    }
  }
  if (d === 0) return { text: 'Arriving today', tone: 'success', daysUntil: 0 }
  if (d === 1) return { text: 'Arriving tomorrow', tone: 'info', daysUntil: 1 }
  return { text: `Expected ${humanDate(o.expected_date)}`, tone: 'neutral', daysUntil: d }
}

/** Most-urgent-first, mirroring the Vendors directory: a late delivery beats
 *  everything, then today's arrivals, then approvals blocking the pipeline,
 *  then orders ready to go out, then future arrivals by date. History sorts
 *  newest first and always sinks below active work. */
export function poUrgencyRank(
  o: Pick<PurchaseOrderSummary, 'status' | 'expected_date' | 'receipt_date'>,
  today: Date,
): number {
  const bucket = poBucket(o.status)
  if (bucket === 'history') return 1000
  if (bucket === 'arriving') {
    const info = poExpectedInfo(o, today)
    if (info.daysUntil != null && info.daysUntil < 0) return 0 // late
    if (info.daysUntil === 0) return 1 // today
    return 4
  }
  if (bucket === 'needs_approval') return 2
  return 3 // ready_to_send
}

export function poQueueCompare(a: PurchaseOrderSummary, b: PurchaseOrderSummary, today: Date): number {
  const ra = poUrgencyRank(a, today)
  const rb = poUrgencyRank(b, today)
  if (ra !== rb) return ra - rb
  if (ra === 1000) {
    // History: newest completed first.
    return (b.receipt_date ?? b.created_at).localeCompare(a.receipt_date ?? a.created_at)
  }
  // Same urgency: nearest expected date first, dateless last, then newest.
  const da = poExpectedInfo(a, today).daysUntil
  const db = poExpectedInfo(b, today).daysUntil
  if (da != null && db != null && da !== db) return da - db
  if (da != null && db == null) return -1
  if (da == null && db != null) return 1
  return b.created_at.localeCompare(a.created_at)
}
